import React, { useCallback, useEffect, useMemo, useState } from "react";
import AtriumApi, { getApiErrorMessage } from "api";
import notify from "lib/notify";
import { MdAdd, MdEdit, MdDelete, MdPublic } from "react-icons/md";
import { FaFacebook, FaInstagram, FaLinkedin, FaYoutube, FaTiktok } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { PageContainer, PageHeader } from "components/page";
import Card from "components/card";
import Button, { IconButton } from "components/button";
import Dialog from "components/dialog";
import { useConfirm } from "components/dialog/ConfirmProvider";
import DataTable from "components/data-table";
import { InputField, Select } from "components/fields";
import { EmptyState, ErrorState } from "components/states";

const SOCIAL_LINK_FORM_ID = "social-link-form";

const PLATFORMS = {
  facebook: {
    name: "Facebook",
    icon: FaFacebook,
    placeholder: "https://facebook.com/yourpage",
  },
  twitter: {
    name: "Twitter/X",
    icon: FaXTwitter,
    placeholder: "https://twitter.com/yourhandle",
  },
  instagram: {
    name: "Instagram",
    icon: FaInstagram,
    placeholder: "https://instagram.com/yourhandle",
  },
  linkedin: {
    name: "LinkedIn",
    icon: FaLinkedin,
    placeholder: "https://linkedin.com/company/yourcompany",
  },
  youtube: {
    name: "YouTube",
    icon: FaYoutube,
    placeholder: "https://youtube.com/@yourchannel",
  },
  tiktok: {
    name: "TikTok",
    icon: FaTiktok,
    placeholder: "https://tiktok.com/@yourhandle",
  },
  other: {
    name: "Other",
    icon: MdPublic,
    placeholder: "https://example.com",
  },
};

const PLATFORM_OPTIONS = Object.entries(PLATFORMS).map(([value, platform]) => ({
  value,
  label: platform.name,
}));

const platformName = (link) => PLATFORMS[link.platform]?.name || link.platform;

const SocialLinkForm = ({ link, onSave }) => {
  const [formData, setFormData] = useState(() => ({
    platform: link?.platform || "facebook",
    url: link?.url || "",
  }));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form id={SOCIAL_LINK_FORM_ID} onSubmit={handleSubmit} className="flex flex-col gap-4">
      <Select
        label="Platform"
        name="platform"
        value={formData.platform}
        onChange={handleInputChange}
        options={PLATFORM_OPTIONS}
        required
      />
      <InputField
        label="URL"
        type="url"
        name="url"
        value={formData.url}
        onChange={handleInputChange}
        placeholder={PLATFORMS[formData.platform]?.placeholder}
        required
      />
    </form>
  );
};

const SocialLinksManagement = () => {
  const confirm = useConfirm();
  const [socialLinks, setSocialLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);

  const fetchSocialLinks = useCallback(async () => {
    setLoadError(null);
    try {
      const response = await AtriumApi.get("/connect/social-links");
      if (response.data.success) {
        setSocialLinks(response.data.data || []);
      }
    } catch (error) {
      setLoadError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSocialLinks();
  }, [fetchSocialLinks]);

  const retryFetch = () => {
    setLoading(true);
    fetchSocialLinks();
  };

  const handleAddLink = () => {
    setEditingLink(null);
    setModalOpen(true);
  };

  const handleEditLink = useCallback((link) => {
    setEditingLink(link);
    setModalOpen(true);
  }, []);

  const closeModal = () => {
    setModalOpen(false);
    setEditingLink(null);
  };

  const handleSaveLink = async (formData) => {
    setSaving(true);
    try {
      const response = editingLink
        ? await AtriumApi.put(`/connect/social-links/${editingLink.id}`, formData)
        : await AtriumApi.post("/connect/social-links", formData);

      if (response.data.success) {
        notify.success(editingLink ? "Social link updated" : "Social link added");
        closeModal();
        fetchSocialLinks();
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not save social link"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLink = useCallback(
    async (link) => {
      const confirmed = await confirm({
        title: `Delete "${platformName(link)}" link?`,
        message: "This link will be removed from your public Link Hub.",
        confirmLabel: "Delete",
        tone: "danger",
      });
      if (!confirmed) return;

      setSaving(true);
      try {
        const response = await AtriumApi.delete(`/connect/social-links/${link.id}`);
        if (response.data.success) {
          notify.success("Social link deleted");
          fetchSocialLinks();
        }
      } catch (error) {
        notify.error(getApiErrorMessage(error, "Could not delete social link"));
      } finally {
        setSaving(false);
      }
    },
    [confirm, fetchSocialLinks]
  );

  const columns = useMemo(
    () => [
      {
        id: "platform",
        header: "Platform",
        cell: ({ row }) => {
          const link = row.original;
          const Icon = (PLATFORMS[link.platform] || PLATFORMS.other).icon;
          return (
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-surface-subtle text-fg">
                <Icon aria-hidden="true" className="h-5 w-5" />
              </span>
              <span className="font-medium text-fg">{platformName(link)}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "url",
        header: "URL",
        cell: ({ row }) => (
          <a
            href={row.original.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block max-w-md truncate text-sm text-fg-muted hover:underline"
          >
            {row.original.url}
          </a>
        ),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        meta: { align: "right" },
        cell: ({ row }) => {
          const link = row.original;
          const name = platformName(link);
          return (
            <div className="flex justify-end gap-1">
              <IconButton
                icon={<MdEdit />}
                label={`Edit ${name} link`}
                onClick={() => handleEditLink(link)}
              />
              <IconButton
                icon={<MdDelete />}
                label={`Delete ${name} link`}
                onClick={() => handleDeleteLink(link)}
                disabled={saving}
              />
            </div>
          );
        },
      },
    ],
    [handleEditLink, handleDeleteLink, saving]
  );

  const addButton = (
    <Button leftIcon={<MdAdd />} onClick={handleAddLink} disabled={saving}>
      Add Social Link
    </Button>
  );

  const renderContent = () => {
    if (loadError) {
      return (
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(loadError, "Could not load social links")}
            onRetry={retryFetch}
          />
        </Card>
      );
    }
    if (!loading && socialLinks.length === 0) {
      return (
        <Card padding="md">
          <EmptyState
            icon={MdPublic}
            title="No Social Links Yet"
            description="Add your social media profiles to display on your Link Hub."
            action={addButton}
          />
        </Card>
      );
    }
    return (
      <div className="flex flex-col gap-2">
        {!loading && (
          <p className="text-sm text-fg-muted">
            {socialLinks.length} {socialLinks.length === 1 ? "link" : "links"} total
          </p>
        )}
        <DataTable
          aria-label="Social links"
          columns={columns}
          data={socialLinks}
          loading={loading}
          getRowId={(link) => String(link.id)}
        />
      </div>
    );
  };

  return (
    <PageContainer>
      <PageHeader
        title="Social Links"
        description="Add and manage your social media links"
        actions={!loadError && addButton}
      />

      {renderContent()}

      <Dialog
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingLink ? "Edit Social Link" : "Add Social Link"}
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" form={SOCIAL_LINK_FORM_ID} loading={saving}>
              {editingLink ? "Save Changes" : "Create Link"}
            </Button>
          </>
        }
      >
        <SocialLinkForm link={editingLink} onSave={handleSaveLink} />
      </Dialog>
    </PageContainer>
  );
};

export default SocialLinksManagement;
